"use client";

import Link from "next/link";
import { useMemo, type CSSProperties } from "react";

import DynamicIcon from "@/components/atoms/DynamicIcon";

/**
 * Banner 3001 — KUK auxiliary page hero (kukonline.ac.in `/admission-policy`).
 *
 * Ported from `hoc/AuxiliaryPage.tsx` in kuk-campus-portal: the hero every
 * auxiliary page on that site renders above its article body. A full-width
 * background image inside a 1120px wrapper, a white gradient overlay that
 * fades out to the right, and a vertically centred title with an optional row
 * of link icons.
 *
 * v1 is a fixed, non-responsive block — one background image, one set of
 * offsets, one type size at every width — so this port has no breakpoints
 * either. The only additions are the CMS fields (image, title, colours,
 * heights) and an optional mobile image, which falls back to the desktop one
 * and so changes nothing when it is left empty.
 *
 * Notes on v1 details that are deliberately reproduced or dropped:
 *
 * BACKGROUND — a CSS `background-image` (`bg-cover` / `center center` /
 * `bg-no-repeat`), not a `next/image`, exactly as v1 paints it.
 *
 * GRADIENT OVERLAY — `linear-gradient(270deg, …)` on a `rotate-180` layer.
 * 270deg runs the gradient line right-to-left and the 180° rotation flips it
 * back, so the opaque stop lands on the LEFT, behind the title, and fades out
 * to the right. The pair is kept as-is rather than "simplified" to 90deg,
 * because the two transforms also mirror the 37.35% stop position.
 *
 * COLOURS — v1 reaches these through its own Tailwind palette
 * (`text-secondary-900` = #031F44, and the icon colour hardcoded to the same
 * value). This app's `secondary` scale is a different palette, so they resolve
 * to literals and become CMS fields instead.
 *
 * DROPPED — v1's `bg-lightgray` class. `lightgray` is not in that project's
 * Tailwind colour scale, so the class never compiled to anything; carrying it
 * over would add a background v1 does not actually paint.
 *
 * DROPPED — v1's hardcoded fallback banner (`pages.kukonline.in/…png`). A
 * tenant-specific asset has no place in a shared widget; with no image
 * configured the section paints the gradient overlay alone.
 */

/**
 * v1: `rgba(255, 255, 255, 0.92)`, kept verbatim. Both gradient stops are RGBA
 * fields rather than COLOR ones — the admin colour picker is a 6-digit hex box
 * with no alpha channel, so an 8-digit hex default would render correctly but
 * could never be edited back to itself.
 */
const GRADIENT_COLOR_START = "rgba(255, 255, 255, 0.92)";
/** v1: `rgba(255, 255, 255, 0.00)`. */
const GRADIENT_COLOR_END = "rgba(255, 255, 255, 0.00)";
/** v1's gradient stop position, kept off the CMS so the fade cannot be broken. */
const GRADIENT_STOP = "37.35%";
/** v1: `text-secondary-900` on the title and `color="#031F44"` on the icons. */
const INK = "#031F44";
/** v1: `minHeight: "300px"` on the background block. */
const DEFAULT_MIN_HEIGHT = 300;
/** v1: `Wrapper` is `max-w-[1120px] mx-auto`. */
const DEFAULT_MAX_WIDTH = 1120;
/** v1: `width="23" height="23"` on every icon. */
const ICON_SIZE = "23";

interface Banner3001Link {
  id?: string | number;
  /** An uploaded icon URL, or an `iconMap` key — `DynamicIcon` accepts both. */
  icon?: string;
  link?: string;
  /** Accessible name for the link; v1 ships none, so this is additive. */
  label?: string;
  priority?: number;
  status?: boolean;
}

interface Banner3001Data {
  status?: boolean | number | string;
  /** Background image. */
  image?: string;
  /** Optional; falls back to `image`. */
  mobile_image?: string;
  /** v1 puts this on the background block's `aria-label`. */
  image_alt?: string;
  title?: string;
  title_color?: string;
  /** Icon links. v1 has three fixed `link1`–`link3` / `icon1`–`icon3` slots. */
  metadata?: Banner3001Link[];
  /** Overlay gradient — opaque start (left) and transparent end (right). */
  gradient_color_1?: string;
  gradient_color_2?: string;
  icon_color?: string;
  /** Minimum height of the background block in px. */
  hero_section_height?: number;
  /** Wrapper max width in px. */
  hero_container_max_width?: number;
}

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

/** `url("…")` with the quote that would close it escaped out. */
function toCssUrl(src: string): string {
  return `url("${src.replace(/"/g, "%22")}")`;
}

const Banner3001 = ({ data }: { data: any }) => {
  const sectionData: Banner3001Data | undefined = data?.data ?? data;

  const links = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return (
      rows
        // v1 gates each slot on `link1`/`link2`/`link3`, not on the icon — an
        // icon with no href is never painted.
        .filter((row) => row?.status !== false && !!row?.link)
        .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0))
    );
  }, [sectionData?.metadata]);

  if (!sectionData || !toBool(sectionData.status, true)) return null;

  const {
    image = "",
    mobile_image = "",
    image_alt = "",
    title = "",
    title_color = INK,
    gradient_color_1 = GRADIENT_COLOR_START,
    gradient_color_2 = GRADIENT_COLOR_END,
    icon_color = INK,
    hero_section_height = DEFAULT_MIN_HEIGHT,
    hero_container_max_width = DEFAULT_MAX_WIDTH,
  } = sectionData;

  const minHeight = Number(hero_section_height) || DEFAULT_MIN_HEIGHT;
  const maxWidth = Number(hero_container_max_width) || DEFAULT_MAX_WIDTH;

  const desktopImage = image ? toCssUrl(image) : "none";
  const mobileImage = mobile_image ? toCssUrl(mobile_image) : desktopImage;

  return (
    /* v1's `Wrapper`: a centred, clipped section capped at 1120px. */
    <section
      className="relative mx-auto w-full overflow-hidden"
      style={{ maxWidth: `${maxWidth}px` }}
    >
      {/* The image rides in on custom properties so the one CMS field can feed
          both the base rule and the tab+ override; v1 has a single image and
          sets it inline. */}
      <div
        className="relative w-full bg-[image:var(--b3001-bg-mobile)] bg-cover bg-center bg-no-repeat tab:bg-[image:var(--b3001-bg)]"
        style={
          {
            minHeight: `${minHeight}px`,
            "--b3001-bg": desktopImage,
            "--b3001-bg-mobile": mobileImage,
          } as CSSProperties
        }
        aria-label={image_alt || undefined}
      >
        <div
          className="absolute left-0 top-0 h-full w-full rotate-180"
          style={{
            background: `linear-gradient(270deg, ${gradient_color_1} ${GRADIENT_STOP}, ${gradient_color_2} 100%)`,
          }}
        />

        <div className="absolute left-10 top-1/2 z-10 -translate-y-1/2 px-10">
          <h2
            className="text-4xl font-semibold"
            style={{ color: title_color }}
          >
            {title}
          </h2>
          {/* Rendered even when empty, as in v1: its `mt-4` is part of the
              block being vertically centred, so dropping it would nudge the
              title down by 8px on every page that configures no links. */}
          <div className="mt-4 flex justify-start gap-4">
            {links.map((row, index) => (
              <Link
                key={row.id ?? index}
                href={row.link as string}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={row.label || undefined}
                title={row.label || undefined}
              >
                <DynamicIcon
                  iconName={row.icon || ""}
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  color={icon_color}
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Banner3001;
